import { useState, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Camera,
  Mail,
  Phone,
  Calendar,
  Clock,
  ShieldCheck,
  Edit,
  Check,
  X,
  Loader2,
  UserCircle2,
  ArrowRight,
  Box,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores'
import { updateProfile, type UpdateProfileRequest } from '@/services/userProfile'
import { uploadMedia } from '@/services/media'
import { getSubscriptionTransaction, type SubscriptionTransaction } from '@/services/subscription'
import { queryKeys } from '@/lib/query/keys'
import { useCompany } from '@/hooks/use-company'

export const Route = createFileRoute('/(app)/profile/')({
  component: ProfilePage,
})

// Common timezones
const TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'America/New_York', label: 'America/New_York (EST)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST)' },
]

const SUBSCRIPTION_LOGIN_URL = 'https://subscription.easydo365.com/login'

// Status options
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'away', label: 'Away' },
  { value: 'busy', label: 'Busy' },
  { value: 'offline', label: 'Offline' },
]

function ProfilePage() {
  const user = useAuthStore((state) => state.user)
  const setAuth = useAuthStore((state) => state.setAuth)
  const token = useAuthStore((state) => state.token)
  const { companies, isLoading: isLoadingCompanies } = useCompany() // Get all companies user is associated with

  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    user_name: user?.user_name || '',
    email: user?.email || '',
    dob: user?.dob || '',
    user_time_zone: user?.user_time_zone || '',
    user_status: user?.user_status || 'active',
  })
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (response) => {
      if (response.success && user && token) {
        // Update the auth store with new user data
        const updatedUser = {
          ...user,
          user_name: formData.user_name,
          email: formData.email,
          dob: formData.dob,
          user_time_zone: formData.user_time_zone,
          user_status: formData.user_status,
          ...(previewImage && { image: previewImage }),
        }
        setAuth(token, updatedUser)
        toast.success('Profile updated successfully')
        setIsEditing(false)
        setPreviewImage(null)
        setSelectedFile(null)
      }
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to update profile')
    },
  })

  // Upload media mutation
  const uploadMediaMutation = useMutation({
    mutationFn: uploadMedia,
  })

  // Get user initials for avatar fallback
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Format date for display
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return 'Not set'
    }
  }

  // Format date for input
  const formatDateForInput = (dateStr: string | null) => {
    if (!dateStr) return ''
    try {
      const date = new Date(dateStr)
      return date.toISOString().split('T')[0]
    } catch {
      return ''
    }
  }

  // Handle edit mode toggle
  const handleEditClick = () => {
    if (!isEditing) {
      setFormData({
        user_name: user?.user_name || '',
        email: user?.email || '',
        dob: user?.dob || '',
        user_time_zone: user?.user_time_zone || '',
        user_status: user?.user_status || 'active',
      })
    }
    setIsEditing(true)
  }

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false)
    setPreviewImage(null)
    setSelectedFile(null)
    setFormData({
      user_name: user?.user_name || '',
      email: user?.email || '',
      dob: user?.dob || '',
      user_time_zone: user?.user_time_zone || '',
      user_status: user?.user_status || 'active',
    })
  }

  // Handle save
  const handleSave = async () => {
    if (!user) return

    try {
      let imageUrl = user.image

      // Upload image if a new one was selected
      if (selectedFile) {
        const deviceId = user.user_device_id && user.user_device_id.trim().length > 0
          ? user.user_device_id
          : 'WEB_CLIENT'
        const timezone = user.user_time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone

        const uploadResponse = await uploadMediaMutation.mutateAsync({
          device_id: deviceId,
          user_id: user.id,
          timezone,
          files: [selectedFile],
        })

        if (uploadResponse?.result?.[0]?.original_file) {
          imageUrl = uploadResponse.result[0].original_file
        }
      }

      const request: UpdateProfileRequest = {
        user_id: user.id,
        user_name: formData.user_name,
        email: formData.email,
        dob: formData.dob,
        time_zone: formData.user_time_zone,
        user_status: formData.user_status,
        ...(imageUrl !== user.image && { image: imageUrl }),
      }

      updateProfileMutation.mutate(request)
    } catch {
      toast.error('Failed to upload image')
    }
  }

  // Handle image selection
  const handleImageClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB')
        return
      }

      setSelectedFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const isLoading = updateProfileMutation.isPending || uploadMediaMutation.isPending

  // Fetch subscription data
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useQuery({
    queryKey: queryKeys.subscription.transaction(),
    queryFn: async () => {
      const response = await getSubscriptionTransaction({})
      // Log to verify we're getting all data and check for role fields
      console.log('Subscription API Response:', response)
      console.log('Total subscriptions fetched:', response?.data?.length || 0)
      if (response?.data) {
        response.data.forEach((sub, index) => {
          console.log(`Subscription ${index + 1}:`, {
            id: sub.id,
            user_id: sub.user_id,
            company_name: sub.company_name,
            package_name: sub.package_name,
            role: sub.role,
            user_role: sub.user_role,
            allKeys: Object.keys(sub)
          })
        })
      }
      return response
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  })

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading profile...</div>
      </div>
    )
  }

  const displayImage = previewImage || user.image

  // Calculate days left for a subscription
  const calculateDaysLeft = (endDate: string): number => {
    try {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      // Handle date strings with time (e.g., "2026-11-08 12:54:33")
      const end = new Date(endDate.replace(' ', 'T'))
      end.setHours(0, 0, 0, 0)
      const diffTime = end.getTime() - today.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return Math.max(0, diffDays)
    } catch {
      return 0
    }
  }

  // Format date for card display (e.g., "Oct 8, 2024")
  const formatDateForCard = (dateStr: string): string => {
    try {
      // Handle date strings with time (e.g., "2024-10-08 12:54:33")
      const date = new Date(dateStr.replace(' ', 'T'))
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  // Get status config based on days left (prioritize actual days left over API status)
  const getStatusConfig = (daysLeft: number) => {
    // Only show red if actually expired (0 days left), not just based on API status
    if (daysLeft === 0) {
      return {
        bgGradient: 'from-red-500/10 to-red-600/5',
        borderColor: 'border-red-500/20',
        badgeClass: 'bg-red-500/10 text-red-600 border-red-500/20',
        ringColor: 'ring-red-500/20',
        accentColor: 'text-red-600',
        progressBg: 'bg-red-500',
      }
    }
    // Show orange/amber for subscriptions expiring soon (less than 60 days)
    if (daysLeft < 60) {
      return {
        bgGradient: 'from-amber-500/10 to-orange-500/5',
        borderColor: 'border-amber-500/20',
        badgeClass: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
        ringColor: 'ring-amber-500/20',
        accentColor: 'text-amber-600',
        progressBg: 'bg-amber-500',
      }
    }
    // Show green for subscriptions with plenty of time left (60+ days)
    return {
      bgGradient: 'from-emerald-500/10 to-teal-500/5',
      borderColor: 'border-emerald-500/20',
      badgeClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
      ringColor: 'ring-emerald-500/20',
      accentColor: 'text-emerald-600',
      progressBg: 'bg-emerald-500',
    }
  }

  // Check if subscription has Company module available
  const hasCompanyModule = (subscription: SubscriptionTransaction): boolean => {
    // Check module_id for module "1" (Company module)
    // module_id is a comma-separated string like "1,2,3,4,5..."
    if (subscription.module_id) {
      const moduleIds = String(subscription.module_id).split(',').map(id => id.trim())
      if (moduleIds.includes('1')) {
        return true
      }
    }
    
    // Also check access_module_object for company-related modules as fallback
    const accessModules = subscription.access_module_object || {}
    const companyModules = [
      'employee_onboarding',
      'multi_branch_creation',
      'attendance',
      'leave_management',
      'salary_slip',
    ]
    
    // Check if any company module is available in access_module_object
    const hasModuleInAccess = companyModules.some((module) => {
      return accessModules[module] === 'Y'
    })
    
    return hasModuleInAccess
  }

  // Determine role based on company_id and employee role
  const getSubscriptionRole = (subscription: SubscriptionTransaction): 'Owner' | 'Admin' | 'Employee' => {
    const hasCompanyMod = hasCompanyModule(subscription)
    
    console.log('Determining role for subscription:', {
      subscriptionId: subscription.id,
      subscriptionUserId: subscription.user_id,
      currentUserId: user.id,
      companyId: subscription.company_id,
      companyName: subscription.company_name,
      moduleId: subscription.module_id,
      hasCompanyModule: hasCompanyMod,
      companiesAvailable: companies.length,
    })

    // Step 1: Check if user is Owner
    // If subscription belongs to current user (user_id matches) and has no company_id
    if (subscription.user_id === user.id && (!subscription.company_id || subscription.company_id === '' || subscription.company_id === null)) {
      console.log('Role determined: Owner (user owns subscription directly)')
      return 'Owner'
    }

    // If subscription has company_id, find the company and check user's role
    if (subscription.company_id && subscription.company_id !== '' && subscription.company_id !== null) {
      const companyId = Number(subscription.company_id)
      const company = companies.find((c) => {
        return c.id === companyId || String(c.id) === String(companyId) || String(c.id) === subscription.company_id
      })
      
      if (company) {
        const role = company.role || company.company_role?.role || ''
        const roleLower = role.toLowerCase()
        
        // Check if user is Owner
        const isOwner = roleLower.includes('owner') || roleLower.includes('founder') || roleLower.includes('creator')
        
        if (isOwner) {
          console.log('Role determined: Owner (from company role)')
          return 'Owner'
        }
      }
    }

    // Check owner_company_details for ownership
    if (subscription.owner_company_details?.company && Array.isArray(subscription.owner_company_details.company)) {
      const ownerCompanies = subscription.owner_company_details.company
      const userOwnsCompany = ownerCompanies.some((comp: { company_id: string; company_name: string; total_employee: string }) => {
        const compId = Number(comp.company_id)
        const userCompany = companies.find((c) => c.id === compId || String(c.id) === String(compId))
        if (userCompany) {
          const role = userCompany.role || userCompany.company_role?.role || ''
          const roleLower = role.toLowerCase()
          return roleLower.includes('owner') || roleLower.includes('founder') || roleLower.includes('creator')
        }
        return false
      })
      
      if (userOwnsCompany) {
        console.log('Role determined: Owner (from owner_company_details)')
        return 'Owner'
      }
    }

    // Fallback: if user_id matches, likely Owner
    if (subscription.user_id === user.id) {
      console.log('Role determined: Owner (fallback - user_id matches)')
      return 'Owner'
    }
    
    // Step 2: If NOT Owner, check if Company module is available
    // If Company module is available (module_id includes "1"), show Employee
    if (hasCompanyMod) {
      console.log('Role determined: Employee (Company module available, user is not Owner)')
      return 'Employee'
    }
    
    // Step 3: Default to Admin if not Owner and no Company module
    console.log('Role determined: Admin (not Owner, no Company module)')
    return 'Admin'
  }

  return (
    <div className="container max-w-4xl py-8 space-y-8">
      {/* Profile Header Card */}
      <Card className="overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" />
        <CardContent className="relative pb-8">
          {/* Avatar Section */}
          <div className="absolute -top-16 left-8">
            <div className="relative group">
              <Avatar
                className={`h-32 w-32 border-4 border-background shadow-xl ${isEditing ? 'cursor-pointer' : ''}`}
                onClick={handleImageClick}
              >
                <AvatarImage
                  src={displayImage || undefined}
                  alt={user.user_name || 'User'}
                />
                <AvatarFallback className="text-3xl bg-primary text-primary-foreground">
                  {getUserInitials(user.user_name || 'User')}
                </AvatarFallback>
              </Avatar>
              {isEditing && (
                <Button
                  size="icon"
                  variant="secondary"
                  className="absolute bottom-0 right-0 h-10 w-10 rounded-full shadow-lg"
                  onClick={handleImageClick}
                >
                  <Camera className="h-5 w-5" />
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          {/* User Info */}
          <div className="pt-20 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-2">
                  <Label htmlFor="user_name">Full Name</Label>
                  <Input
                    id="user_name"
                    value={formData.user_name}
                    onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                    placeholder="Enter your name"
                    className="max-w-sm"
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold">{user.user_name || 'User'}</h1>
                    {user.is_document_verified === 'Y' && (
                      <Badge variant="secondary" className="gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Verified
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-1">
                    {user.email || user.phone_no || 'No contact info'}
                  </p>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    className="gap-2"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button variant="outline" className="gap-2" onClick={handleEditClick}>
                  <Edit className="h-4 w-4" />
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                </div>
                {isEditing ? (
                  <div className="flex-1 max-w-md space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                    />
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user.email || 'Not provided'}</p>
                  </div>
                )}
              </div>
              {!isEditing && user.is_email_verified === 'Y' && user.email && (
                <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
                  Verified
                </Badge>
              )}
            </div>
            <Separator className="mt-6" />
          </div>

          {/* Phone */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">
                    {user.phone_no || 'Not provided'}
                  </p>
                </div>
              </div>
              {user.phone_no && (
                <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
                  Verified
                </Badge>
              )}
            </div>
            <Separator className="mt-6" />
          </div>

          {/* Date of Birth */}
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Calendar className="h-5 w-5 text-muted-foreground" />
              </div>
              {isEditing ? (
                <div className="flex-1 max-w-md space-y-2">
                  <Label htmlFor="dob">Date of Birth</Label>
                  <Input
                    id="dob"
                    type="date"
                    value={formatDateForInput(formData.dob)}
                    onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  />
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{formatDate(user.dob)}</p>
                </div>
              )}
            </div>
            <Separator className="mt-6" />
          </div>

          {/* Timezone */}
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Clock className="h-5 w-5 text-muted-foreground" />
              </div>
              {isEditing ? (
                <div className="flex-1 max-w-md space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={formData.user_time_zone}
                    onValueChange={(value) => setFormData({ ...formData, user_time_zone: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground">Timezone</p>
                  <p className="font-medium">{user.user_time_zone || 'Not set'}</p>
                </div>
              )}
            </div>
            <Separator className="mt-6" />
          </div>

          {/* Status */}
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <UserCircle2 className="h-5 w-5 text-muted-foreground" />
              </div>
              {isEditing ? (
                <div className="flex-1 max-w-md space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.user_status}
                    onValueChange={(value) => setFormData({ ...formData, user_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium capitalize">{user.user_status || 'Active'}</p>
                  </div>
                  <div
                    className={`h-2.5 w-2.5 rounded-full ${
                      user.user_status === 'active' ? 'bg-green-500' : 
                      user.user_status === 'away' ? 'bg-yellow-500' :
                      user.user_status === 'busy' ? 'bg-red-500' : 'bg-gray-400'
                    }`}
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Status */}
      <Card>
        <CardHeader>
          <CardTitle>Account Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <ShieldCheck
                className={`h-5 w-5 ${
                  user.is_document_verified === 'Y'
                    ? 'text-green-500'
                    : 'text-muted-foreground'
                }`}
              />
              <div>
                <p className="text-sm text-muted-foreground">Document</p>
                <p className="font-medium">
                  {user.is_document_verified === 'Y' ? 'Verified' : 'Not Verified'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <ShieldCheck
                className={`h-5 w-5 ${
                  user.two_step_verification === 'Y'
                    ? 'text-green-500'
                    : 'text-muted-foreground'
                }`}
              />
              <div>
                <p className="text-sm text-muted-foreground">2FA</p>
                <p className="font-medium">
                  {user.two_step_verification === 'Y' ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <Mail
                className={`h-5 w-5 ${
                  user.is_email_verified === 'Y'
                    ? 'text-green-500'
                    : 'text-muted-foreground'
                }`}
              />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">
                  {user.is_email_verified === 'Y' ? 'Verified' : 'Not Verified'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Subscription Status */}
      <Card>
        <CardHeader>
          <CardTitle>Current Subscription Status</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingSubscription ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading subscription...</span>
            </div>
          ) : subscriptionData?.data && subscriptionData.data.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {subscriptionData.data.map((subscription) => {
                // Parse dates correctly (handle format "2024-10-08 12:54:33")
                const startDate = subscription.subscription_availed_date?.replace(' ', 'T') || subscription.subscription_availed_date
                const endDate = subscription.subscription_end_date?.replace(' ', 'T') || subscription.subscription_end_date
                
                const daysLeft = calculateDaysLeft(subscription.subscription_end_date)
                // Only determine role if companies are loaded
                const role = isLoadingCompanies ? 'Admin' : getSubscriptionRole(subscription)
                const isOwner = role === 'Owner'
                // Only consider expired if actually 0 days left (not just API status)
                const isExpired = daysLeft === 0
                
                // Use company_name if available, otherwise show "Your Subscription"
                const title = subscription.company_name?.trim() 
                  ? subscription.company_name 
                  : 'Your Subscription'
                
                const startDateFormatted = formatDateForCard(subscription.subscription_availed_date)
                const endDateFormatted = formatDateForCard(subscription.subscription_end_date)
                
                // Calculate progress percentage (how much time has elapsed)
                const totalDays = Math.ceil(
                  (new Date(endDate).getTime() - new Date(startDate).getTime()) /
                    (1000 * 60 * 60 * 24)
                )
                // Progress shows elapsed time, not remaining time
                const elapsedDays = totalDays - daysLeft
                const progressPercent = totalDays > 0 
                  ? Math.max(0, Math.min(100, (elapsedDays / totalDays) * 100))
                  : 0
                
                const config = getStatusConfig(daysLeft)
                
                // Debug log
                console.log('Subscription card data:', {
                  id: subscription.id,
                  title,
                  status: subscription.status,
                  daysLeft,
                  startDate: subscription.subscription_availed_date,
                  endDate: subscription.subscription_end_date,
                  totalDays,
                  elapsedDays,
                  progressPercent,
                  role,
                  isExpired
                })

                return (
                  <div
                    key={subscription.id}
                    className={`group relative overflow-hidden rounded-2xl border ${config.borderColor} bg-gradient-to-br ${config.bgGradient} p-6 transition-all duration-300 hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/20 hover:-translate-y-1 min-w-0 flex flex-col`}
                  >
                    {/* Background decoration */}
                    <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-white/5 to-transparent blur-2xl" />

                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex-1 min-w-0">
                        {/* Package name badge */}
                        {subscription.package_name && (
                          <div className="mb-2">
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground whitespace-nowrap">
                              <Box className="h-3 w-3 text-foreground" />
                              {subscription.package_name}
                            </span>
                          </div>
                        )}
                        <h3 className="font-semibold text-foreground truncate text-base leading-tight">
                          {title}
                        </h3>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${config.badgeClass} whitespace-nowrap`}
                          >
                            {role}
                          </span>
                          {isExpired && (
                            <span className="inline-flex items-center rounded-full bg-destructive/15 px-2.5 py-0.5 text-xs font-medium text-destructive whitespace-nowrap">
                              Expired
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Days counter */}
                      <div className="text-right flex-shrink-0">
                        <div className={`text-3xl xl:text-4xl font-bold tabular-nums ${config.accentColor} leading-tight`}>
                          {daysLeft}
                        </div>
                        <div className="text-xs text-muted-foreground font-medium whitespace-nowrap">days left</div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-auto mb-4">
                      <div className="h-2 w-full overflow-hidden rounded-full border border-border bg-muted/50">
                        <div
                          className={`h-full rounded-full ${config.progressBg} transition-all duration-500`}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-auto flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 flex-1">
                        <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">
                          {startDateFormatted} → {endDateFormatted}
                        </span>
                      </div>

                      {isOwner && !isExpired && (
                        <button 
                          onClick={() => window.open('https://subscription.easydo365.com/', '_blank')}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-800 whitespace-nowrap flex-shrink-0"
                        >
                          Upgrade
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      )}

                      {isExpired && (
                        <Button
                          asChild
                          variant="destructive"
                          size="sm"
                          className="text-xs rounded-lg flex-shrink-0"
                        >
                          <a href={SUBSCRIPTION_LOGIN_URL}>Renew Now</a>
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">No subscription found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
